import type {
  APMAsset,
  CogniteTimeseries,
  ICDFView,
} from '@cognite/apm-client';
import type {
  CogniteClient,
  Datapoints,
  DirectRelationReference,
  ExternalDatapoint,
  TimeSeries,
  Timeseries,
  TimeseriesFilter,
  ViewReference,
} from '@cognite/sdk';

// TODO(FO-3044): The datapoint service is initialized directly in some components.
// If a CDM duplicate is created, it needs to be updated in multiple places.
import type {
  DatapointDeleteResponse,
  DatapointInsertResponse,
} from './cdm-types';
import { DatapointService } from './datapoint-service';
import {
  TIMESERIES_ASSET_EXTERNAL_ID_KEY,
  timeseriesFilterToFdmFilter,
} from './timeseries-idm-migrator';
import type { ITimeseriesService } from './timeseries-service-interface';
import { filterDatapointStrings, filterTimeseriesStrings } from './utils';

export type CogniteClientDependencies = {
  timeseries: Pick<
    CogniteClient['timeseries'],
    'retrieve' | 'search' | 'list' | 'update' | 'create'
  >;
  datapoints: Pick<
    CogniteClient['datapoints'],
    'insert' | 'delete' | 'retrieveLatest' | 'retrieve'
  >;
  relationships: Pick<CogniteClient['relationships'], 'list'>;
  post: CogniteClient['post'];
  project: string;
  instances: Pick<CogniteClient['instances'], 'upsert'>;
};

export type APMClientDependencies = {
  /**
   * Timeseries service is optional for legacy reasons, Todo(FO-3045)
   */
  timeseries: Pick<
    ICDFView<Timeseries>,
    'list' | 'search' | 'defaultInstanceSpace' | 'viewReference'
  >;
  assets: Pick<ICDFView<APMAsset>, 'defaultInstanceSpace'>;
  appDataInstanceSpace?: string;
  sourceDataInstanceSpace?: string;
};

/** DMS nested timeseries nodes include `space`; SDK Timeseries only has it on instanceId. */
const getTimeseriesInstanceSpace = (
  timeseries: TimeSeries
): string | undefined => {
  if (!('space' in timeseries)) {
    return undefined;
  }
  const { space } = timeseries;
  return typeof space === 'string' ? space : undefined;
};
export class CdmTimeseriesService implements ITimeseriesService {
  client;
  apmClient: APMClientDependencies;
  datapointService;
  appDataInstanceSpace: string;
  sourceDataInstanceSpaces: string[];
  spaces: string[];
  viewReference: ViewReference;
  rootLocation?: DirectRelationReference;
  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies,
    sourceDataInstanceSpaces: string[],
    appDataInstanceSpace: string,
    viewReference: ViewReference,
    rootLocation?: DirectRelationReference
  ) {
    this.appDataInstanceSpace = appDataInstanceSpace;
    this.sourceDataInstanceSpaces = sourceDataInstanceSpaces;
    this.spaces = [appDataInstanceSpace, ...sourceDataInstanceSpaces];
    this.client = client;
    this.apmClient = apmClient;
    this.datapointService = new DatapointService(client);
    this.rootLocation = rootLocation;
    this.viewReference = viewReference;
  }

  async getTimeseriesByExternalId(externalId: string): Promise<Timeseries> {
    const { items } = await this.apmClient.timeseries.list({
      filters: {
        equals: {
          property: 'externalId',
          eq: externalId,
        },
      },
      spaces: this.spaces,
      pageSize: 1,
    });

    return items[0];
  }

  async upsertTimeseries(
    timeseries: Partial<Timeseries>,
    _rootAssetId?: number
  ): Promise<Timeseries[]> {
    // TODO(FO-3041): Support multiple assets - currently if a timeseries has multiple assets it will be overwritten

    if (!this.rootLocation) {
      throw new Error('Missing root location for timeseries service');
    }
    if (!timeseries.externalId) {
      throw new Error('Timeseries externalId is required');
    }

    const assetExternalId =
      timeseries.metadata?.[TIMESERIES_ASSET_EXTERNAL_ID_KEY];

    // Unwrap assets from { items: [...] } to plain array for API
    const timeseriesForApi: Omit<
      CogniteTimeseries,
      'space' | 'externalId' | 'unit' | 'createdTime' | 'lastUpdatedTime'
    > = {
      assets: assetExternalId
        ? [
            {
              externalId: assetExternalId,
              space: this.apmClient.assets.defaultInstanceSpace,
            },
          ]
        : [this.rootLocation],
      type: timeseries.isString ? 'string' : 'numeric',
      sourceUnit: timeseries.unit,
      isStep: timeseries.isStep,
      description: timeseries.description,
      name: timeseries.name,
    };

    const { items } = await this.client.instances.upsert({
      items: [
        {
          instanceType: 'node',
          externalId: timeseries.externalId,
          space: this.appDataInstanceSpace,
          sources: [
            {
              source: this.apmClient.timeseries.viewReference,
              properties: timeseriesForApi,
            },
          ],
        },
      ],
    });

    const upsertedItem = items[0];
    if (!upsertedItem) {
      throw new Error('Upsert failed: no items returned from API');
    }

    // Return the timeseries in the expected format with instanceId set
    // so that it can be used to link to the measurement
    // TODO(FO-3042): Only return data from the API response, not from the input timeseries object
    return [
      {
        ...timeseries,
        isString: timeseries.isString ?? false,
        isStep: timeseries.isStep ?? false,
        description: timeseries.description ?? '',
        id: timeseries.id ?? 0,
        instanceId: {
          externalId: upsertedItem.externalId,
          space: upsertedItem.space,
        },
        createdTime: new Date(upsertedItem.createdTime),
        lastUpdatedTime: new Date(upsertedItem.lastUpdatedTime),
      },
    ];
  }

  // TODO(FO-3048): Move to DatapointService
  async insertDataPoint(
    timeseriesExternalId: string,
    dataPoint: ExternalDatapoint
  ): Promise<DatapointInsertResponse> {
    const { data } = await this.client.post<DatapointInsertResponse>(
      `api/v1/projects/${this.client.project}/timeseries/data`,
      {
        data: {
          items: [
            {
              instanceId: {
                externalId: timeseriesExternalId,
                space: this.appDataInstanceSpace,
              },
              datapoints: [dataPoint],
            },
          ],
        },
      }
    );
    return data;
  }

  // TODO(FO-3048): Move to DatapointService
  async deleteDataPoint(
    timeseriesExternalId: string,
    timestamp: number
  ): Promise<{ data: DatapointDeleteResponse }> {
    return this.client.post<DatapointDeleteResponse>(
      `api/v1/projects/${this.client.project}/timeseries/data/delete`,
      {
        data: {
          items: [
            {
              instanceId: {
                externalId: timeseriesExternalId,
                space: this.appDataInstanceSpace,
              },
              inclusiveBegin: timestamp,
              exclusiveEnd: timestamp + 1,
            },
          ],
        },
      }
    );
  }

  async getTimeseriesByAsset(
    assetExternalId: string,
    filter: TimeseriesFilter = {},
    limit?: number
  ): Promise<Timeseries[]> {
    const finalFilters: TimeseriesFilter = {
      assetExternalIds: [assetExternalId],
      isString: false,
      ...filter,
    };
    const { items } = await this.apmClient.timeseries.list({
      filters: timeseriesFilterToFdmFilter(
        this.apmClient.assets.defaultInstanceSpace,
        finalFilters
      ),
      spaces: this.spaces,
      pageSize: limit,
    });

    return filterTimeseriesStrings(items);
  }

  async getTimeseriesByRelationships(
    _assetExternalId: string,
    _limit?: number
  ): Promise<Timeseries[]> {
    // In IDM we will not use relationships to get timeseries
    return [];
  }

  // the max limit for client.datapoints is 100 .retrieveLatest
  async getTimeseriesLatestDatapoints(
    timeseries: TimeSeries[]
  ): Promise<Datapoints[]> {
    if (timeseries.length === 0) {
      return [];
    }
    // TODO(FO-3043): Support multiple timeseries with potentially different spaces
    if (timeseries.length > 1) {
      throw new Error(
        'Multiple timeseries are not supported in case they have multiple spaces'
      );
    }
    const firstTimeseries = timeseries[0];
    if (firstTimeseries.isString) {
      return [];
    }
    // Migrated timeseries use instanceId; nested DMS nodes (e.g. checklist measurements) expose
    // externalId + space on the relation payload without running TimeseriesIDMMigrator.
    const externalId =
      firstTimeseries.instanceId?.externalId ?? firstTimeseries.externalId;
    const space =
      firstTimeseries.instanceId?.space ??
      getTimeseriesInstanceSpace(firstTimeseries) ??
      this.appDataInstanceSpace;

    if (!externalId) {
      throw new Error('Timeseries externalId is required');
    }

    const datapoint = await this.datapointService.retrieveLatest(
      [externalId],
      space
    );
    return filterDatapointStrings(datapoint);
  }

  async searchTimeseries(
    query: string,
    limit?: number,
    filters?: TimeseriesFilter
  ): Promise<Timeseries[]> {
    const timeseries: Timeseries[] = await this.apmClient.timeseries.search({
      query,
      filters: timeseriesFilterToFdmFilter(
        this.apmClient.assets.defaultInstanceSpace,
        filters,
        true
      ),
      searchOperator: 'AND',
      // TODO(FO-3046): Investigate what overrides what - spaces or filters
      spaces: this.spaces,
      pageSize: limit,
    });

    return filterTimeseriesStrings(timeseries);
  }

  getViewReference(): ViewReference {
    return this.viewReference;
  }
}

export const createCdmTimeseriesService = (
  ...args: ConstructorParameters<typeof CdmTimeseriesService>
): ITimeseriesService => new CdmTimeseriesService(...args);
